import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { clsx } from 'clsx';
import { useLanguage } from '../../../features/language/useLanguage';
import { UnitToggle } from '../../common/UnitToggle';
import { WeightInput } from '../../common/WeightInput';
import { formatRupiah, parseRupiah } from '../../../utils/format';
import type { InventoryModalFormData } from './types';

interface Props {
  unit: 'kg' | 'gram';
  toggleUnit: () => void;
  readOnly: boolean;
}

export const SellingPackagingSection: React.FC<Props> = ({ unit, toggleUnit, readOnly }) => {
  const { t } = useLanguage();
  const { control, setValue, formState: { errors } } = useFormContext<InventoryModalFormData>();
  const estimatedSellingPrice = useWatch({ control, name: 'estimatedSellingPrice' });

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('inventory.modal.sections.sellingPackaging')}</h2>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <div className="flex justify-between items-center mb-1 h-7">
            <label className="block text-sm font-medium text-gray-700">
              {t('inventory.modal.fields.estimatedSellingPrice')}
            </label>
            <div className="invisible"><UnitToggle unit={unit} onToggle={() => {}} /></div>
          </div>
          <input
            type="text"
            inputMode="numeric"
            value={formatRupiah(estimatedSellingPrice)}
            onChange={(e) => {
              const raw = parseRupiah(e.target.value);
              setValue('estimatedSellingPrice', raw, { shouldValidate: true, shouldDirty: true });
            }}
            className={clsx(
              'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
              errors.estimatedSellingPrice ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            )}
            data-test-id="inventory-create-estimated-selling-price"
            disabled={readOnly}
          />
          {errors.estimatedSellingPrice && <p className="mt-1 text-xs text-red-600">{errors.estimatedSellingPrice.message}</p>}
        </div>
        <div>
          <div className="flex justify-between items-center mb-1 h-7">
            <label className="block text-sm font-medium text-gray-700">
              {t('inventory.modal.fields.packSize')}
            </label>
            <UnitToggle unit={unit} onToggle={toggleUnit} disabled={readOnly} />
          </div>
          <Controller
            control={control}
            name="packSizeGrams"
            render={({ field: { value, onChange } }) => (
              <WeightInput
                value={value !== undefined && value !== null ? value / 1000 : undefined}
                onChange={(valKg) => onChange(valKg !== undefined ? valKg * 1000 : undefined)}
                unit={unit}
                disabled={readOnly}
                className={clsx(
                  'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
                  errors.packSizeGrams ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                )}
                placeholder={t('inventory.modal.fields.packSizePlaceholder')}
                data-test-id="inventory-create-pack-size"
              />
            )}
          />
          {errors.packSizeGrams && <p className="mt-1 text-xs text-red-600">{errors.packSizeGrams.message}</p>}
        </div>
      </div>
    </div>
  );
};
